import type { TypedDocumentNode } from '@apollo/client';
import { gql, useLazyQuery } from '@apollo/client';
import { useEffect, useEffectEvent, useState } from 'react';

import type { KycDocumentRequirement } from '#src/__generated__/globalTypes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlCountries } from '#src/hooks/useIntlCountries.ts';

import { ChooseDocument } from './ChooseDocument/index.tsx';
import type { Props as UploadProps } from './Upload/index.tsx';
import { Upload } from './Upload/index.tsx';
import type {
  KYCDocumentRequirementsQuery,
  KYCDocumentRequirementsQueryVariables,
} from './__generated__/index.graphql.ts';

const KYC_DOCUMENT_REQUIREMENTS_QUERY = gql`
  query KYCDocumentRequirementsQuery($countryCode: String!) {
    mangopay {
      id
      kycDocumentRequirements(countryCode: $countryCode) {
        documentType
        nbPages
      }
    }
  }
` as TypedDocumentNode<
  KYCDocumentRequirementsQuery,
  KYCDocumentRequirementsQueryVariables
>;

export enum ProofOfIdStep {
  CHOOSE_DOCUMENT = 'CHOOSE_DOCUMENT',
  UPLOAD = 'UPLOAD',
}

type ProofOfIdentityFormProps = Omit<
  UploadProps,
  'onBack' | 'documentRequirement'
> & {
  onBack: () => void;
  onStepChange?: (newStep: ProofOfIdStep) => void;
  cotrolledUploadProofOfIdStep?: ProofOfIdStep;
};

export const ProofOfIdentityForm = ({
  onBack,
  onStepChange,
  cotrolledUploadProofOfIdStep,
  ...uploadProps
}: ProofOfIdentityFormProps) => {
  const [uploadProofOfIdStep, setUploadProofOfIdStep] = useState<ProofOfIdStep>(
    ProofOfIdStep.CHOOSE_DOCUMENT
  );
  const { fiatWalletAccountable } = useCurrentUserContext();
  const countries = useIntlCountries(true);

  const currentUserCountryOfResidence = countries.find(
    c => c.value === fiatWalletAccountable?.countryOfResidenceCode
  );

  const [issuingCountry, setIssuingCountry] = useState<
    { value: string; label: string } | undefined
  >(currentUserCountryOfResidence);
  const [kycDocumentRequirements, setKycDocumentRequirements] = useState<
    KycDocumentRequirement[]
  >([]);
  const [documentRequirement, setDocumentRequirement] =
    useState<KycDocumentRequirement>();

  const [query, { loading }] = useLazyQuery(KYC_DOCUMENT_REQUIREMENTS_QUERY);

  useEffect(() => {
    if (
      cotrolledUploadProofOfIdStep &&
      cotrolledUploadProofOfIdStep !== uploadProofOfIdStep
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUploadProofOfIdStep(cotrolledUploadProofOfIdStep);
    }
  }, [cotrolledUploadProofOfIdStep, uploadProofOfIdStep]);

  const fetchKycRequirements = useEffectEvent(async (countryCode: string) => {
    const result = await query({
      variables: { countryCode },
    });

    if (result.data) {
      const { kycDocumentRequirements: requirements } = result.data.mangopay;
      setKycDocumentRequirements(requirements);
      setDocumentRequirement(requirements[0]);
    }
  });

  useEffect(() => {
    if (issuingCountry?.value) {
      fetchKycRequirements(issuingCountry.value);
    }
  }, [issuingCountry?.value]);

  const uploadProofOfIdStepCb = (newStep: ProofOfIdStep) => {
    setUploadProofOfIdStep(newStep);
    onStepChange?.(newStep);
  };

  if (uploadProofOfIdStep === ProofOfIdStep.UPLOAD && documentRequirement)
    return (
      <Upload
        documentRequirement={documentRequirement}
        onBack={() => {
          uploadProofOfIdStepCb(ProofOfIdStep.CHOOSE_DOCUMENT);
        }}
        {...uploadProps}
      />
    );

  return (
    <ChooseDocument
      issuingCountry={issuingCountry}
      setIssuingCountry={setIssuingCountry}
      kycDocumentRequirements={kycDocumentRequirements}
      kycDocumentRequirementsLoading={loading}
      documentRequirement={documentRequirement}
      setDocumentRequirement={setDocumentRequirement}
      onBack={onBack}
      onNext={() => {
        uploadProofOfIdStepCb(ProofOfIdStep.UPLOAD);
      }}
    />
  );
};
